import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

/**
 * BreadcrumbPopButton —— 面包屑条尾部的「报告 / 总览」按钮 + 浮窗（通用版）。
 *
 * 由「项目财务」页的 CollectionsReportButton 泛化而来（2026-10-16）：
 *   同一个交互形态被复用到「工程项目」页（开工倒计时 / 本周检查日历 / AI 部门晨报 / 阶段分布图），
 *   故抽成通用组件：按钮文案、图标、testid 参数化，按钮 + 浮窗外壳 + 自动展开 + 缩放动画的行为**保持一致**。
 *
 * 行为（与财务页原按钮完全一致）：
 *   - 显示为一个带 icon 的按钮，点击从按钮那一角放出 / 收起浮窗（复用 Radix Popover 的
 *     `origin-[--radix-popover-content-transform-origin]` + `bc-report-pop` 动画，align=end 让缩放原点落在按钮这一角）；
 *   - 电脑进入页面自动展开一次（autoOpen，默认 true）；手机按需点击，避免每次进入都被报告遮住列表。
 *     注：2026-10-23 起「工程项目 / TeamBoard」的工程总览改为**点击才显示**（autoOpen={false}），
 *     不再进页面自动弹窗（浮窗宽 1400px，会盖住整张看板）。此处默认值保持 true 以兼容后续新增调用方。
 *
 * 为什么由页面 portal 注入面包屑插槽（见 context/BreadcrumbSlot.js）：
 *   浮窗内容仍活在**页面组件树**里，因此能拿到页面状态（如当前筛选条件）并保留原有联动
 *   （点阶段柱状图筛列表、点日历项目跳详情、开工卡的分配/开始操作等）。
 *
 * 用法：内容通过 children 传入，本组件只负责「按钮 + 浮窗外壳 + 自动展开 + 动画」。
 *
 * props：
 *   - label：按钮文案（由调用方按语言算好）
 *   - icon：按钮图标组件（lucide）
 *   - testid：按钮 data-testid（浮窗为 `${testid}-pop`，右上关闭为 `${testid}-close`）
 *   - title / subtitle：浮窗头部标题与副标题（由调用方按语言算好）
 *   - popClassName：浮窗额外类名（宽度 / 高度上限 / 滚动等）
 *   - autoOpen：进入页面是否自动展开一次（默认 true）
 *   - children：浮窗内容
 */
export default function BreadcrumbPopButton({
  label,
  icon: Icon,
  testid = "bc-pop",
  title,
  subtitle,
  popClassName = "",
  autoOpen = true,
  children,
}) {
  const [open, setOpen] = useState(false);

  // 手机进入项目/财务页先看列表，报告仍可从同一入口打开；不修改桌面自动展开偏好。
  useEffect(() => { if (autoOpen && !window.matchMedia("(max-width: 767px)").matches) setOpen(true); }, [autoOpen]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-expanded={open}
          title={label}
          className={`inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors ${open ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-600"}`}
        >
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </PopoverTrigger>
      {/* align=end + `bc-report-pop` 动画（见 index.css）→ 浮窗从按钮那一角吸进 / 放出 */}
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className={`bc-report-pop rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-2xl shadow-slate-900/25 ${popClassName}`}
        data-testid={`${testid}-pop`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
          </div>
          <button type="button" aria-label="Close" data-testid={`${testid}-close`} onClick={() => setOpen(false)} className="h-11 w-11 shrink-0 flex items-center justify-center rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100">✕</button>
        </div>
        {children}
      </PopoverContent>
    </Popover>
  );
}
